import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { 
  FileText, 
  Clock, 
  DollarSign, 
  CheckCircle2, 
  XCircle, 
  MessageSquare,
  ThumbsUp,
  ThumbsDown,
  AlertCircle,
  Loader2,
  Send,
  User,
  Building2,
  Calendar,
  Package
} from 'lucide-react';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import { storedFixedBidQuote } from '../utils/fixedBidMarkup';

interface ClientSOWViewerProps {
  shareToken: string;
}

interface SOWData {
  id: string;
  projectTitle: string;
  clientName: string;
  companyName: string;
  scope: string;
  deliverables: string[];
  timeline: string;
  startDate: string;
  endDate: string;
  pricing: {
    model: string;
    hourlyRate?: number;
    fixedPrice?: number;
    estimatedHours?: number;
    total?: number;
    /** A fixed bid's contingency (markup): `total` includes it. */
    basePrice?: number;
    markupPercent?: number;
    markupAmount?: number;
  };
  assumptions: string[];
  exclusions: string[];
  paymentTerms: string;
  status: string;
  version: number;
}

interface ShareInfo {
  id: string;
  message?: string;
  allowComments: boolean;
  allowApproval: boolean;
  sharedAt: string;
}

interface Comment {
  id: string;
  authorName: string;
  authorRole: 'consultant' | 'client';
  section: string;
  content: string;
  createdAt: string;
  resolved: boolean;
}

export function ClientSOWViewer({ shareToken }: ClientSOWViewerProps) {
  const [loading, setLoading] = useState(true);
  const [sow, setSOW] = useState<SOWData | null>(null);
  const [share, setShare] = useState<ShareInfo | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [selectedSection, setSelectedSection] = useState<string>('general');
  const [clientName, setClientName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState<'approved' | 'changes_requested' | 'rejected' | null>(null);
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSOW();
    loadComments();
  }, [shareToken]);

  const loadSOW = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/sow/${shareToken}`
      );

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Failed to load SOW');
        return;
      }

      const data = await response.json();
      setSOW(data.sow);
      setShare(data.share);
    } catch (err) {
      console.error('Error loading SOW:', err);
      setError('Failed to load SOW. Please check the link and try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/sow/${shareToken}/comments`
      );

      if (response.ok) {
        const data = await response.json();
        setComments(data.comments || []);
      }
    } catch (err) {
      console.error('Error loading comments:', err);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    if (!clientName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    try {
      setSubmitting(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/sow/${shareToken}/comment`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            authorName: clientName,
            authorRole: 'client',
            section: selectedSection,
            content: newComment,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to add comment');
      }

      toast.success('Comment added successfully');
      setNewComment('');
      loadComments();
    } catch (err) {
      console.error('Error adding comment:', err);
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproval = async (status: 'approved' | 'changes_requested' | 'rejected') => {
    if (!clientName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    if (status !== 'approved' && !feedback.trim()) {
      toast.error('Please provide feedback for your decision');
      return;
    }

    try {
      setSubmitting(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/sow/${shareToken}/approve`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            status,
            approvedByName: clientName,
            feedback: feedback || undefined,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to submit approval');
      }

      setApprovalStatus(status);
      toast.success(
        status === 'approved'
          ? 'SOW approved'
          : status === 'rejected'
          ? 'SOW rejected. The consultant will be notified.'
          : 'Changes requested. The consultant will be notified.'
      );
    } catch (err) {
      console.error('Error submitting approval:', err);
      toast.error('Failed to submit approval');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="text-center">
          <Loader2 className="size-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-muted-foreground">Loading Statement of Work...</p>
        </div>
      </div>
    );
  }

  if (error || !sow || !share) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="size-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Unable to Load SOW</h2>
              <p className="text-muted-foreground mb-4">{error}</p>
              <p className="text-sm text-muted-foreground">
                Please contact the sender for a valid link.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const sectionComments = comments.filter(c => c.section === selectedSection);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-8 px-4">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <Card className="border-t-4 border-t-blue-600">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <CardTitle className="text-2xl mb-2">{sow.projectTitle}</CardTitle>
                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Building2 className="size-4" />
                    {sow.companyName}
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="size-4" />
                    Shared {formatDate(share.sharedAt)}
                  </div>
                  <Badge variant="outline">Version {sow.version}</Badge>
                </div>
              </div>
              <Badge 
                className={
                  sow.status === 'approved' ? 'bg-green-100 text-green-700' :
                  sow.status === 'rejected' ? 'bg-red-100 text-red-700' :
                  sow.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-700'
                }
              >
                {sow.status}
              </Badge>
            </div>
            
            {share.message && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm">{share.message}</p>
              </div>
            )}
          </CardHeader>
        </Card>

        {/* Project Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="size-5 text-blue-600" />
              Project Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Timeline */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground">Start Date</Label>
                <p className="font-medium">{formatDate(sow.startDate)}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">End Date</Label>
                <p className="font-medium">{formatDate(sow.endDate)}</p>
              </div>
            </div>

            <Separator />

            {/* Scope of Work */}
            <div>
              <h3 className="font-semibold mb-2 flex items-center justify-between">
                Scope of Work
                {share.allowComments && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedSection('scope')}
                  >
                    <MessageSquare className="size-4 mr-1" />
                    Comment
                  </Button>
                )}
              </h3>
              <p className="text-sm whitespace-pre-wrap">{sow.scope}</p>
            </div>

            <Separator />

            {/* Deliverables */}
            <div>
              <h3 className="font-semibold mb-2 flex items-center justify-between">
                Deliverables
                {share.allowComments && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedSection('deliverables')}
                  >
                    <MessageSquare className="size-4 mr-1" />
                    Comment
                  </Button>
                )}
              </h3>
              <ul className="space-y-2">
                {sow.deliverables.map((deliverable, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="size-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>{deliverable}</span>
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Pricing */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="size-5 text-green-600" />
              Pricing & Payment
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground">Pricing Model</Label>
                <p className="font-medium capitalize">{sow.pricing.model.replace('_', ' ')}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Total Investment</Label>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(sow.pricing.total || 0)}
                </p>
                {(() => {
                  const quote = storedFixedBidQuote(sow.pricing);
                  return quote ? (
                    <p className="text-xs text-muted-foreground mt-1" data-testid="client-sow-contingency">
                      Base price {formatCurrency(quote.base)} plus a {quote.percent}% contingency of {formatCurrency(quote.markup)}
                    </p>
                  ) : null;
                })()}
              </div>
            </div>

            {sow.pricing.estimatedHours && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Estimated Hours</Label>
                  <p className="font-medium">{sow.pricing.estimatedHours}h</p>
                </div>
                {sow.pricing.hourlyRate && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Hourly Rate</Label>
                    <p className="font-medium">{formatCurrency(sow.pricing.hourlyRate)}/hr</p>
                  </div>
                )}
              </div>
            )}

            <Separator />

            <div>
              <Label className="text-xs text-muted-foreground">Payment Terms</Label>
              <p className="text-sm">{sow.paymentTerms}</p>
            </div>
          </CardContent>
        </Card>

        {/* Assumptions */}
        {sow.assumptions.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Assumptions</span>
                {share.allowComments && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedSection('assumptions')}
                  >
                    <MessageSquare className="size-4 mr-1" />
                    Comment
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {sow.assumptions.map((assumption, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <span className="text-muted-foreground">•</span>
                    <span>{assumption}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Exclusions */}
        {sow.exclusions.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Out of Scope (Exclusions)</span>
                {share.allowComments && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedSection('exclusions')}
                  >
                    <MessageSquare className="size-4 mr-1" />
                    Comment
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {sow.exclusions.map((exclusion, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <XCircle className="size-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <span>{exclusion}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Comments Section */}
        {share.allowComments && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="size-5 text-purple-600" />
                Comments & Questions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Existing Comments */}
              {sectionComments.length > 0 && (
                <div className="space-y-3 mb-4">
                  {sectionComments.map((comment) => (
                    <div 
                      key={comment.id}
                      className={`p-3 rounded-lg ${
                        comment.authorRole === 'consultant'
                          ? 'bg-blue-50 border border-blue-200'
                          : 'bg-purple-50 border border-purple-200'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <User className="size-4" />
                        <span className="font-medium text-sm">{comment.authorName}</span>
                        <Badge variant="outline" className="text-xs">
                          {comment.authorRole}
                        </Badge>
                        <span className="text-xs text-muted-foreground ml-auto">
                          {formatDate(comment.createdAt)}
                        </span>
                      </div>
                      <p className="text-sm">{comment.content}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Comment Form */}
              <div className="space-y-3">
                <div>
                  <Label htmlFor="name">Your Name</Label>
                  <Input
                    id="name"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder="Enter your name"
                  />
                </div>
                <div>
                  <Label htmlFor="section">Section</Label>
                  <select
                    id="section"
                    className="w-full p-2 border rounded-md"
                    value={selectedSection}
                    onChange={(e) => setSelectedSection(e.target.value)}
                  >
                    <option value="general">General</option>
                    <option value="scope">Scope of Work</option>
                    <option value="deliverables">Deliverables</option>
                    <option value="pricing">Pricing</option>
                    <option value="timeline">Timeline</option>
                    <option value="assumptions">Assumptions</option>
                    <option value="exclusions">Exclusions</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="comment">Your Comment or Question</Label>
                  <Textarea
                    id="comment"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Ask a question or leave a comment..."
                    rows={3}
                  />
                </div>
                <Button 
                  onClick={handleAddComment} 
                  disabled={submitting}
                  className="w-full"
                >
                  {submitting ? (
                    <Loader2 className="size-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="size-4 mr-2" />
                  )}
                  Post Comment
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Approval Section */}
        {share.allowApproval && !approvalStatus && (
          <Card className="border-2 border-purple-200">
            <CardHeader>
              <CardTitle>Review & Approve</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="approval-name">Your Name *</Label>
                <Input
                  id="approval-name"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="Enter your name"
                />
              </div>
              
              <div>
                <Label htmlFor="feedback">Feedback (required for changes/rejection)</Label>
                <Textarea
                  id="feedback"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Share your thoughts or requested changes..."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <Button
                  onClick={() => handleApproval('approved')}
                  disabled={submitting}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <ThumbsUp className="size-4 mr-2" />
                  Approve
                </Button>
                <Button
                  onClick={() => handleApproval('changes_requested')}
                  disabled={submitting}
                  variant="outline"
                >
                  <AlertCircle className="size-4 mr-2" />
                  Request Changes
                </Button>
                <Button
                  onClick={() => handleApproval('rejected')}
                  disabled={submitting}
                  variant="destructive"
                >
                  <ThumbsDown className="size-4 mr-2" />
                  Reject
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Approval Confirmation */}
        {approvalStatus && (
          <Card className={`border-2 ${
            approvalStatus === 'approved' ? 'border-green-500 bg-green-50' :
            approvalStatus === 'rejected' ? 'border-red-500 bg-red-50' :
            'border-yellow-500 bg-yellow-50'
          }`}>
            <CardContent className="pt-6 text-center">
              {approvalStatus === 'approved' ? (
                <CheckCircle2 className="size-16 text-green-600 mx-auto mb-3" />
              ) : approvalStatus === 'rejected' ? (
                <XCircle className="size-16 text-red-600 mx-auto mb-3" />
              ) : (
                <AlertCircle className="size-16 text-yellow-600 mx-auto mb-3" />
              )}
              <h3 className="text-xl font-bold mb-2">
                {approvalStatus === 'approved' ? 'SOW Approved' :
                 approvalStatus === 'rejected' ? 'SOW Rejected' :
                 'Changes Requested'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {approvalStatus === 'approved' 
                  ? 'Thank you. The consultant has been notified of your approval.'
                  : 'The consultant has been notified and will follow up with you.'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}